"use client";
import React from "react";
import Image from "next/image";
import { LegalFooter } from "@/components/shared/LegalFooter";
interface AuthLayoutProps {
    children: React.ReactNode;
    subtitle?: string;
}

/**
 * First screen a signed-out visitor sees. Layout follows the prototype's
 * "Getting in" start screen (2026-09-20-savira-e2e-v3): a light page, the
 * brand top left, one centred column with a headline and a
 * single carved card holding the form.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ children, subtitle }) => {
    return (
        <div className="min-h-screen bg-bg px-4 text-ink font-sans">
            <div className="mx-auto w-full max-w-[1200px] pt-3.5 pb-20">
                {/* Chrome: brand */}
                <div className="flex min-h-11 flex-wrap items-center gap-x-[18px] gap-y-2.5">
                    <Image
                        src="/logo-dark.png"
                        alt="Savira Logo"
                        width={140}
                        height={41}
                        className="savira-logo-ink h-8 w-auto object-contain mix-blend-multiply"
                        priority
                    />
                    {/* Dark theme: the white logo instead. */}
                    <Image
                        src="/logo-light.png"
                        alt="Savira Logo"
                        width={140}
                        height={41}
                        className="hidden h-8 w-auto object-contain dark:block"
                        loading="eager"
                    />
                </div>

                {/* One column */}
                <div className="mx-auto mt-10 w-full max-w-[560px] animate-fade-in-up sm:mt-16">
                    <h1 className="mb-3.5 text-balance text-[clamp(22px,3vw,26px)] font-extrabold leading-[1.2] tracking-[-0.02em] text-ink">
                        AI that makes <span className="text-accent">your</span> business run better.
                    </h1>

                    <div className="rounded-xl border border-line bg-card p-[18px] sm:p-6">
                        {children}
                    </div>

                    {subtitle && (
                        <p className="mt-3.5 text-[13px] font-semibold text-ink-3">
                            {subtitle}
                        </p>
                    )}

                    <div className="mt-3 [&>footer]:justify-start">
                        <LegalFooter variant="light" />
                    </div>
                </div>
            </div>

            <style jsx global>{`
                @keyframes fade-in-up {
                    from { opacity: 0.3; transform: translateY(4px); }
                    to { opacity: 1; transform: none; }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.25s ease forwards;
                }
            `}</style>
        </div>
    );
};
